import { AcceptCookieBanner } from '@atoms/AcceptCookie';
import { MonitoringNotification } from '@atoms/Monitoring/MonitoringNotification';
import { Public_Routes } from '@common/url';
import { FINANCE_BASE, METADATA_BASE, PROFESSIONAL_BASE } from '@common/urlBasePath';
import { VerticalAppProvider } from '@common/VerticalApps/VerticalAppContext';
import { useQualifiedChatBox } from '@pages/auth/common';
import { MaintenanceUpgradeStatusList } from '@pages/Maintenance/constants';
import { MaintenanceNotification } from '@pages/Maintenance/MaintenanceNotification';
import { useDownServiceStatus } from '@pages/Maintenance/util';
import { Stack, TypographyGlobalStyle } from '@prophecy/ui-v3';
import { history, useLocationPathName } from '@prophecy/utils/history';
import DocumentTitle from '@prophecy/utils/react/DocumentTitle';
import { withSentryReactRouterV6Routing } from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navigate, Outlet, Route, Routes, unstable_HistoryRouter as HistoryRouter } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';

import {
  DatabrickSignUp,
  EmbeddedSignup,
  LoginUsingToken,
  Maintenance,
  OAuth2Consent,
  OAuthCallback,
  OAuthSamlCallback,
  PublicLayout,
  ResetPasswordForm,
  ServiceDown,
  SignInForm,
  SignUpForm
} from '../App';
import { useAppMetadata } from '../context/appMetadata';
import { useUnsecuredGlobalVariables } from '../context/globalConfig/unsecuredConfigHook';
import { useTabFocusEvents } from '../context/hooks/useTabFocusEvents';
import { getPageTitle, NoMatch, PageContainer, StyledRoot } from './common';

const queryClient = new QueryClient();

const RouteTemplate = () => {
  useQualifiedChatBox();
  useTabFocusEvents();
  const preLoginData = useAppMetadata();
  const updateStatus = preLoginData?.maintenanceUpdateInfo?.upgrade_status?.status;
  const isUnderMaintenance = updateStatus && MaintenanceUpgradeStatusList.indexOf(updateStatus) !== -1;
  const pathname = useLocationPathName();
  let OutletComponent = Outlet;
  const isProphecyDown = useDownServiceStatus()?.isProphecyDown;
  //Maintenance page should get the priority than prophecy down
  if (isUnderMaintenance && pathname !== Public_Routes.Maintenance.getUrl()) {
    OutletComponent = () => <Navigate replace to={Public_Routes.Maintenance.getUrlWithBasePath()} />;
  } else if (isProphecyDown && !isUnderMaintenance && pathname !== Public_Routes.ProphecyDown.getUrl()) {
    OutletComponent = () => <Navigate replace to={Public_Routes.ProphecyDown.getUrlWithBasePath()} />;
  }

  return (
    <>
      <DocumentTitle title={getPageTitle()} />
      {!isUnderMaintenance && (
        <>
          <MaintenanceNotification />
          <MonitoringNotification />
        </>
      )}
      <StyledRoot>
        <Stack width='100%' direction='horizontal'>
          <PageContainer>
            <OutletComponent />
          </PageContainer>
        </Stack>
      </StyledRoot>
    </>
  );
};

const SentryRoutes = withSentryReactRouterV6Routing(Routes);

const PublicRoutes = function () {
  const isSMTPEnabled = useUnsecuredGlobalVariables().isSMTPEnabled;
  const { isVerticalAppsEnabled } = useUnsecuredGlobalVariables();

  const ALLOWED_BASES = isVerticalAppsEnabled ? [METADATA_BASE, FINANCE_BASE, PROFESSIONAL_BASE] : [METADATA_BASE];
  // ProphecyDown and Maintenance must be reachable regardless of isVerticalAppsEnabled,
  // because getUrlWithBasePath() redirects to the current base (e.g. /professional) even
  // when that base is not in ALLOWED_BASES. Register them for all three bases unconditionally.
  const ALL_BASES = [METADATA_BASE, FINANCE_BASE, PROFESSIONAL_BASE];

  return (
    <QueryClientProvider client={queryClient}>
      <VerticalAppProvider isVerticalApp={isVerticalAppsEnabled}>
        <TypographyGlobalStyle />
        <AcceptCookieBanner />
        <HistoryRouter history={history}>
          <SentryRoutes>
            <Route
              key={Public_Routes.EmbeddedSignup.url}
              path={METADATA_BASE + Public_Routes.EmbeddedSignup.url}
              element={
                <PublicLayout>
                  <EmbeddedSignup />
                </PublicLayout>
              }
            />
            <Route element={<RouteTemplate />}>
              {ALLOWED_BASES.map((base) => (
                <Fragment key={base}>
                  <Route
                    path={base + Public_Routes.SignIn.url}
                    element={
                      <PublicLayout>
                        <SignInForm />
                      </PublicLayout>
                    }
                  />

                  <Route
                    path={base + Public_Routes.SignUp.url}
                    element={
                      <PublicLayout>
                        <SignUpForm />
                      </PublicLayout>
                    }
                  />
                  {isSMTPEnabled && (
                    <Route
                      path={base + Public_Routes.ResetPassword.url}
                      element={
                        <PublicLayout>
                          <ResetPasswordForm />
                        </PublicLayout>
                      }
                    />
                  )}
                </Fragment>
              ))}

              {ALL_BASES.map((base) => (
                <Fragment key={`service-${base}`}>
                  <Route
                    path={base + Public_Routes.ProphecyDown.url}
                    element={
                      <PublicLayout>
                        <ServiceDown isPublic={true} />
                      </PublicLayout>
                    }
                  />

                  <Route
                    path={base + Public_Routes.Maintenance.url}
                    element={
                      <PublicLayout>
                        <Maintenance />
                      </PublicLayout>
                    }
                  />
                </Fragment>
              ))}

              {ALLOWED_BASES.map((base) => {
                const url = base + Public_Routes.LoginUsingToken.url;
                return (
                  <Route
                    key={url}
                    path={url}
                    element={
                      <PublicLayout>
                        <LoginUsingToken />
                      </PublicLayout>
                    }
                  />
                );
              })}

              <Route
                key={Public_Routes.DatabricksSignUp.url}
                path={METADATA_BASE + Public_Routes.DatabricksSignUp.url}
                element={
                  <PublicLayout>
                    <DatabrickSignUp />
                  </PublicLayout>
                }
              />
            </Route>
            {ALLOWED_BASES.map((base) => (
              <Fragment key={base}>
                <Route
                  path={base + Public_Routes.OauthCallback.url}
                  element={
                    <PublicLayout>
                      <OAuthCallback />
                    </PublicLayout>
                  }
                />
                <Route
                  path={base + Public_Routes.OauthSamlCallback.url}
                  element={
                    <PublicLayout>
                      <OAuthSamlCallback />
                    </PublicLayout>
                  }
                />
              </Fragment>
            ))}

            {ALLOWED_BASES.map((base) => (
              <Route
                key={base + Public_Routes.OauthConsent.url}
                path={base + Public_Routes.OauthConsent.url}
                element={
                  <PublicLayout>
                    <OAuth2Consent />
                  </PublicLayout>
                }
              />
            ))}
            <Route
              path='*'
              element={
                <PublicLayout>
                  <NoMatch />
                </PublicLayout>
              }
            />
          </SentryRoutes>
        </HistoryRouter>
      </VerticalAppProvider>
    </QueryClientProvider>
  );
};

export default PublicRoutes;
